/**
 * The playlist endpoints.
 *
 * A playlist is written as one whole document: `playlistUpsertSchema` carries the
 * complete item array, and the API replaces what it holds with what arrives. That
 * is the same atomicity the table gives — items are embedded in the item, so a
 * reorder is one conditional write — and it is why the editor keeps a local draft
 * and saves it in a single request rather than emitting a call per row. A TV
 * fetching its manifest mid-edit sees either the old list or the new one, never a
 * list halfway through being rebuilt.
 *
 * The record shape read back is pinned here rather than in `schemas.ts` because
 * `packages/shared` pins only the request body; what a playlist looks like on the
 * way out is this panel's own reading of the stored entity.
 *
 * Every route here is addressed by scope, because the table is: a playlist lives
 * in its store's partition or in the shared one, and the API asks which. Reads
 * and deletes carry it as `?storeId=`, writes carry it in the body. There is no
 * "list everything" — the panel fans out over the scopes the caller can reach,
 * which is also the containment rule, since no other store's partition is ever
 * queried on their behalf.
 */

import { z } from 'zod';
import { playlistItemSchema } from '@pbtv/shared';
import type { PlaylistUpsert } from '@pbtv/shared';
import { apiRequest, isApiError, requestJson } from './api.js';
import { collectionOf, entityOf, withIdAlias } from './envelope.js';

export const playlistRecordSchema = withIdAlias(
  z.object({
    playlistId: z.string().min(1),
    /** null is the shared, cross-store library. */
    storeId: z.string().nullish().transform((value) => value ?? null),
    brands: z.array(z.string()).default([]),
    name: z.string(),
    items: z.array(playlistItemSchema).default([]),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
  }),
  'playlistId',
);

export type PlaylistRecord = z.infer<typeof playlistRecordSchema>;

export const playlistListSchema = collectionOf(playlistRecordSchema, 'playlists');
export const playlistEnvelopeSchema = entityOf(playlistRecordSchema, 'playlist', 'detail');

export const PLAYLISTS_QUERY_KEY = ['admin', 'playlists'] as const;

/** One cache entry per library, matching how the routes are addressed. */
export function playlistScopeQueryKey(storeId: string | null): readonly string[] {
  return [...PLAYLISTS_QUERY_KEY, 'scope', storeId ?? 'shared'];
}

/** The shared library is the absence of the parameter, not a magic store id. */
function scopeQuery(storeId: string | null): string {
  return storeId === null ? '' : `?storeId=${encodeURIComponent(storeId)}`;
}

export async function listPlaylists(
  storeId: string | null,
  signal?: AbortSignal,
): Promise<PlaylistRecord[]> {
  return apiRequest(playlistListSchema, `/admin/playlists${scopeQuery(storeId)}`, { signal });
}

/**
 * Every library this caller can reach, in one result.
 *
 * The scopes are fetched together rather than as one query each so the page has a
 * single loading state and a single array whose identity only changes when the
 * data does — a list rebuilt on every render would restart the preview's resolver
 * on every render with it.
 */
export async function listPlaylistsAcross(
  storeIds: readonly (string | null)[],
  signal?: AbortSignal,
): Promise<PlaylistRecord[]> {
  const lists = await Promise.all(storeIds.map((storeId) => listPlaylists(storeId, signal)));
  const byId = new Map<string, PlaylistRecord>();
  for (const playlist of lists.flat()) byId.set(playlist.playlistId, playlist);
  return [...byId.values()];
}

export async function getPlaylist(
  storeId: string | null,
  playlistId: string,
  signal?: AbortSignal,
): Promise<PlaylistRecord> {
  return apiRequest(
    playlistEnvelopeSchema,
    `/admin/playlists/${encodeURIComponent(playlistId)}${scopeQuery(storeId)}`,
    { signal },
  );
}

/**
 * Creates the playlist and returns it when the route answered with the new
 * record. A route that answers 204, or with a shape this build does not
 * recognise, is not a failure: the caller refetches the list either way, and the
 * parsed record is only used to open the editor on the playlist just created
 * instead of making somebody find it in the list.
 */
export async function createPlaylist(input: PlaylistUpsert): Promise<PlaylistRecord | null> {
  const payload = await requestJson('/admin/playlists', { method: 'POST', body: input });
  const parsed = playlistEnvelopeSchema.safeParse(payload);
  return parsed.success ? parsed.data : null;
}

/** Replaces the whole document, items included. The scope travels in the body. */
export async function updatePlaylist(playlistId: string, input: PlaylistUpsert): Promise<void> {
  await requestJson(`/admin/playlists/${encodeURIComponent(playlistId)}`, {
    method: 'PUT',
    body: input,
  });
}

export async function deletePlaylist(storeId: string | null, playlistId: string): Promise<void> {
  await requestJson(
    `/admin/playlists/${encodeURIComponent(playlistId)}${scopeQuery(storeId)}`,
    { method: 'DELETE' },
  );
}

/**
 * The per-item complaints behind a refused save.
 *
 * The API validates every item against the library it can reach and answers with
 * one sentence per problem — "Item 3 has not finished uploading yet". The
 * headline message alone would leave somebody scrolling a twenty-row list
 * looking for which one, so the details are pulled out and shown with it.
 */
export function saveIssues(error: unknown): string[] {
  if (!isApiError(error)) return [];
  const parsed = z
    .object({ error: z.object({ details: z.array(z.string()) }) })
    .safeParse(error.body);
  return parsed.success ? parsed.data.error.details : [];
}
